import { apiBoardView, apiDashboardStats } from 'api/api'
import { BoardDetailType } from 'constants/types'
import { useEffect, useState } from 'react'
import {
	Typography,
	Grid,
	styled,
	Box,
	Card,
	CardContent,
	Button,
	Alert,
} from '@mui/material'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'

import { StyledDataGrid } from 'components/ApplicationStyles'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import PeopleIcon from '@mui/icons-material/People'
import WarningIcon from '@mui/icons-material/Warning'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import LiveHelpIcon from '@mui/icons-material/LiveHelp'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import 'moment/locale/ko'
import { koKR } from '@mui/x-data-grid-premium/locales'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
)

const StatsCard = styled(Card)(({ theme }) => ({
	height: '100%',
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	transition: 'all 0.3s ease',
	// '&:hover': {
	// 	transform: 'translateY(-4px)',
	// 	boxShadow: theme.shadows[8],
	// },
}))

const StatsCardContent = styled(CardContent)(({ theme }) => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(3),
	'&:last-child': {
		paddingBottom: theme.spacing(3),
	},
}))

const IconWrapper = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: 60,
	height: 60,
	borderRadius: '12px',
	marginBottom: theme.spacing(2),
}))

const StatsValue = styled(Typography)(({ theme }) => ({
	fontSize: '2rem',
	fontWeight: 700,
	color: theme.palette.text.primary,
	marginTop: theme.spacing(1),
}))

const StatsLabel = styled(Typography)(({ theme }) => ({
	fontSize: '0.875rem',
	fontWeight: 500,
	color: theme.palette.text.secondary,
	marginBottom: theme.spacing(0.5),
}))

const GridItem = styled(Grid)(({ theme }) => ({
	display: 'flex',
}))

const PageContainer = styled(Grid)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	height: '100%',
}))

const GridContainer = styled(Grid)(({ theme }) => ({
	height: 'calc(100vh - 100px)',
}))

const alertColumns: GridColDef[] = [
	{ field: 'id', headerName: 'ID', width: 70, flex: 1 },
	{ field: 'name', headerName: '이름', width: 130 },
	{
		field: 'state',
		headerName: '상태',
		width: 250,
		flex: 1,
		renderCell: (params) => {
			const { warningCount, dangerCount } = params.row as {
				warningCount: number
				dangerCount: number
			}
			return (
				<>
					<span style={{ color: 'orange' }}>경고 {warningCount}회</span> /{' '}
					<span style={{ color: 'red' }}>위험 {dangerCount}회</span>
				</>
			)
		},
	},
	{ field: 'statDate', headerName: '발생일', width: 110 },
	// {
	// 	field: 'action',
	// 	headerName: '조치',
	// 	width: 150,
	// 	flex: 1,
	// 	renderCell: (params) => (
	// 		<Button
	// 			variant="contained"
	// 			size="small"
	// 			color="inherit"
	// 		>
	// 			<ContentCopyIcon />
	// 		</Button>
	// 	),
	// },
]

const askColumns: GridColDef[] = [
	{ field: 'id', headerName: 'ID', width: 70 },
	{ field: 'type', headerName: '유형', minWidth: 70 },
	{ field: 'title', headerName: '제목', width: 250, flex: 2 },
	{ field: 'name', headerName: '이름', width: 100 },
	{ field: 'createdAt', headerName: '등록일', width: 110 },
]

type DashboardSearchType = {
	startDate: string
	endDate: string
}

const Dashboard = () => {
	const apiRef = useGridApiRef()

	const [isMdUp, setIsMdUp] = useState(window.innerWidth >= 960)

	// React Query로 대시보드 통계 조회
	const {
		data: dashboardStats,
		isLoading,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: ['dashboardStats'],
		queryFn: async () => {
			const result = await apiDashboardStats(search)
			console.log('대시보드 통계 데이터:', result)
			return result
		},
		staleTime: 5 * 60 * 1000, // 5분간 fresh 상태 유지
		gcTime: 10 * 60 * 1000, // 10분간 캐시 유지

		// 자동 리페칭 옵션들
		refetchOnWindowFocus: true, // 윈도우 포커스 시 자동 리페칭 (기본값: true)
		refetchOnReconnect: true, // 네트워크 재연결 시 자동 리페칭 (기본값: true)
		refetchOnMount: true, // 컴포넌트 마운트 시 자동 리페칭 (기본값: true)

		// 주기적 리페칭 (옵션)
		refetchInterval: 5 * 60 * 1000, // 5분마다 자동 리페칭
		refetchIntervalInBackground: false, // 백그라운드에서도 리페칭 (기본값: false)
	})

	useEffect(() => {
		if (dashboardStats) {
			console.log(dashboardStats)
		}
	}, [dashboardStats])

	useEffect(() => {
		const handleResize = () => {
			setIsMdUp(window.innerWidth >= 960)
		}
		window.addEventListener('resize', handleResize)
		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [])

	const [noticeOpen, setNoticeOpen] = useState(false)
	const [noticeItem, setNoticeItem] = useState<BoardDetailType | null>(null)
	const onNoticeClick = (id: number) => {
		// 공지사항 클릭 처리 (예: 상세 페이지로 이동)
		console.log('공지사항 클릭됨, ID:', id)
		apiBoardView(id).then((res) => {
			console.log(res)
			setNoticeItem(res)
			setNoticeOpen(true)
		})
	}

	const [search, setSearch] = useState<DashboardSearchType>({
		startDate: moment().startOf('month').format('YYYY-MM-DD'),
		endDate: moment().endOf('month').format('YYYY-MM-DD'),
	})

	const actionSearch = () => {
		// 검색 처리
		console.log('검색 조건:', search)
		refetch()
	}

	return (
		<PageContainer>
			<GridContainer>
				<Grid
					container
					spacing={3}
					sx={{ p: isMdUp ? 0 : 2 }}
				>
					<Grid
						size={{ xs: 12 }}
						sx={{
							backgroundColor: '#fff',
							boxShadow: 1,
							borderRadius: 1,
						}}
					>
						<Box
							sx={{
								display: isMdUp ? 'inline-flex' : 'block',
								alignItems: 'center',
								gap: 5,
								padding: 2,
								mt: isMdUp ? 0 : 3,
							}}
						>
							<Typography
								variant="h5"
								component="h2"
								gutterBottom
							>
								<b>Dashboard</b>
							</Typography>
							<LocalizationProvider
								dateAdapter={AdapterMoment}
								adapterLocale="ko"
							>
								<Box
									sx={{
										display: 'flex',
										gap: 2,
										alignItems: 'center',
										mt: isMdUp ? 0 : 3,
									}}
								>
									<DatePicker
										label="시작일"
										value={moment(search.startDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearch({ ...search, startDate: newValue.format('YYYY-MM-DD') })
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
									/>
									<Typography>~</Typography>
									<DatePicker
										label="종료일"
										value={moment(search.endDate)}
										onChange={(newValue) => {
											if (newValue) {
												setSearch({ ...search, endDate: newValue.format('YYYY-MM-DD') })
											}
										}}
										slotProps={{ textField: { size: 'small' } }}
									/>
								</Box>
							</LocalizationProvider>
							<Button
								sx={{
									ml: isMdUp ? 'auto' : 0,
									mt: isMdUp ? 0 : 2,
									width: isMdUp ? 'auto' : '100%',
								}}
								variant="contained"
								color="primary"
								onClick={actionSearch}
							>
								검색
							</Button>
						</Box>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<Grid
							container
							spacing={3}
						>
							{/* 총 고객수 */}
							<GridItem size={{ xs: 12, sm: 6, md: 3 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
											}}
										>
											<Box sx={{ flex: 1 }}>
												<StatsLabel>총 이용자 수 / 신규 이용자 수</StatsLabel>
												<StatsValue>
													{dashboardStats?.stat?.userCount?.toLocaleString() ?? '-'} /{' '}
													{dashboardStats?.stat?.newUserCount?.toLocaleString() ?? '-'}
												</StatsValue>
											</Box>
											<IconWrapper sx={{ bgcolor: 'primary.main', color: 'white' }}>
												<PeopleIcon fontSize="large" />
											</IconWrapper>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem>
							{/* 긴급 처리 현황 */}
							<GridItem size={{ xs: 12, sm: 6, md: 3 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
											}}
										>
											<Box sx={{ flex: 1 }}>
												<StatsLabel>위험 상태</StatsLabel>
												<StatsValue>
													{dashboardStats?.stat?.dangerCount?.toLocaleString() ?? '-'}
												</StatsValue>
											</Box>
											<IconWrapper sx={{ bgcolor: 'warning.main', color: 'white' }}>
												<WarningIcon fontSize="large" />
											</IconWrapper>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem>
							{/* AI 콜 관리 */}
							<GridItem size={{ xs: 12, sm: 6, md: 3 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
											}}
										>
											<Box sx={{ flex: 1 }}>
												<StatsLabel>금일 AI콜</StatsLabel>
												<StatsValue>
													{dashboardStats?.stat?.aiCallCount?.toLocaleString() ?? '-'}
												</StatsValue>
											</Box>
											<IconWrapper sx={{ bgcolor: 'success.main', color: 'white' }}>
												<PhoneInTalkIcon fontSize="large" />
											</IconWrapper>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem>
							{/* AI 콜 관리 */}
							<GridItem size={{ xs: 12, sm: 6, md: 3 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
											}}
										>
											<Box sx={{ flex: 1 }}>
												<StatsLabel>미답변 문의</StatsLabel>
												<StatsValue>
													{dashboardStats?.stat?.recentAskCount?.toLocaleString() ?? '-'}
												</StatsValue>
											</Box>
											<IconWrapper sx={{ bgcolor: 'info.main', color: 'white' }}>
												<LiveHelpIcon fontSize="large" />
											</IconWrapper>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem>
							{/* <GridItem size={{ xs: 12, md: 6 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Typography
											variant="h6"
											gutterBottom
										>
											월별 건강 상태 추이
										</Typography>
										<Box sx={{ height: 300 }}>
											<Line
												options={{
													responsive: true,
													maintainAspectRatio: false,
													plugins: {
														legend: {
															display: false,
														},
														title: {
															display: false,
														},
													},
													scales: {
														y: {
															beginAtZero: true,
														},
													},
												}}
												data={{
													labels: [
														'1월',
														'2월',
														'3월',
														'4월',
														'5월',
														'6월',
														'7월',
														'8월',
														'9월',
														'10월',
														'11월',
														'12월',
													],
													datasets: [
														{
															label: '상태',
															data: [65, 59, 80, 81, 56, 55, 40, 72, 88, 95, 102, 110],
															borderColor: 'rgb(75, 192, 192)',
															backgroundColor: 'rgba(75, 192, 192, 0.5)',
															tension: 0.3,
														},
													],
												}}
											/>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem> */}
							<GridItem size={{ xs: 12, md: 6 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Typography
											variant="h6"
											gutterBottom
										>
											위험 알림
										</Typography>
										<Box sx={{ height: isMdUp ? 'calc(100vh - 520px)' : '500px' }}>
											<StyledDataGrid
												apiRef={apiRef}
												columns={alertColumns}
												rows={dashboardStats?.alertList || []}
												showToolbar
												disableRowSelectionOnClick
												loading={isFetching}
												localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
												initialState={{
													columns: {
														columnVisibilityModel: {
															id: false,
														},
													},
												}}
											/>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem>
							<GridItem size={{ xs: 12, md: 6 }}>
								<StatsCard elevation={2}>
									<StatsCardContent>
										<Typography
											variant="h6"
											gutterBottom
										>
											미답변 질문
										</Typography>
										<Box sx={{ height: isMdUp ? 'calc(100vh - 520px)' : '500px' }}>
											<StyledDataGrid
												apiRef={apiRef}
												columns={askColumns}
												rows={dashboardStats?.askList || []}
												showToolbar
												disableRowSelectionOnClick
												loading={isFetching}
												localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
												initialState={{
													columns: {
														columnVisibilityModel: {
															id: false,
														},
													},
												}}
											/>
										</Box>
									</StatsCardContent>
								</StatsCard>
							</GridItem>
							<GridItem size={{ xs: 12 }}>
								<Alert
									severity="warning"
									sx={{ width: '100%' }}
								>
									<Typography>
										<b>알림:</b> 대시보드 통계 데이터는 5분 간격으로 자동 갱신됩니다. 최신
										데이터를 확인하려면 '검색' 버튼을 클릭하세요.
									</Typography>
								</Alert>
							</GridItem>
						</Grid>
					</Grid>
				</Grid>
			</GridContainer>
		</PageContainer>
	)
}
export default Dashboard
